import { useLocalStorage } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import { type Badge, DAILY_TASK_ID, INVENTORY_ID, type Notification, type PlayerStats, SPECIAL_CURRENCY_ID } from '@/types/shared/player'
import api from '@/services/api'
import { useConfig } from '@/composables/game/config'
import { useNow } from '@/composables/game/time'
import { DateTime } from 'luxon'
import type { Nullable } from '@/types/shared/common'
import { useAnimateCoins, useFlyingCoins } from '@/composables/game/flying-coins'
import { formatDuration, getNextOccurrence } from '@/helpers/date'
import { defaultToken, Token } from '@/enums/token'
import { syncTap } from '@/composables/game/tap'
import { buildDropeeStorageKey } from '@/config/config'
import { useAnalytics } from '@/composables/analytics'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { useToaster } from '@/composables/game/toasters'
import { useTranslate } from '@tolgee/vue'
import { type EcommerceItemPrice, MYSTERY_BOX_TYPE, SPECIAL_ECOMMERCE_ITEM_ID } from '@/types/shared/player-config'
import { useUpgrade } from '@/composables/game/upgrade'
import { removeToken } from '@/composables/auth'

export class CloseApp extends Error {
}

export const notifySpinForUtmSource = ref('')
export const wasDormant = ref(false)

export const player = useLocalStorage<PlayerStats>(buildDropeeStorageKey('player'), {
  coins: 0,
  profit: 0,
  specialCurrencies: {},
  pathwayEvents: { runningEvents: {}, instanceNumbers: {}, completionsCount: 0, stepCompletionsCount: 0 },
  rewardLiveOps: { runningEvents: {}, completionsCount: 0, stepCompletionsCount: 0 },
  level: 1,
  experience: 0,
  countryCode: 'XX',
  config: {
    playerId: 0,
    season: '',
    referrerId: null,
  },
  referrals: {
    count: 0,
  },
  friends: {
    count: 0,
    virtualCount: 0,
  },
  lastUpdate: DateTime.utc().toSeconds(),
  energy: {
    level: 0,
    available: 3000,
    max: 3000,
  },
  tap: {
    level: 0,
    earnPerTap: 1,
    coinPerUnit: 1,
  },
  refillBooster: {
    max: 6,
    available: 6,
    firstUseTimestamp: 0,
    lastUseTimestamp: 0,
  },
  upgrades: {},
  challenges: {
    completionsCount: {
      dailyCombo: 0,
    },
  },
  tasks: {
    tonDailyCheckinCredits: 0,
    dappRadarTtmCredits: 0,
    dailyCheckin: {
      lastCheckin: '',
      consecutiveDays: 0,
    },
    counters: {},
    lastDone: {},
    others: {},
  },
  onboarding: {
    done: false,
  },
  enabledFeatures: {},
  featureFlags: {},
  fortuneWheel: {
    prizes: {
      freeUpgrades: 0,
    },
    available: 0,
    lastDailyTicketTimestamp: DateTime.utc().startOf('day').toSeconds(),
  },
  ecommerce: {
    totalSpentCents: 0,
    tonTransactionsCount: 0,
    oneTimePurchases: {},
    lastPurchases: {},
    starsBalance: 0,
    mysteryBoxes: {
      [MYSTERY_BOX_TYPE.FREE]: 0,
      [MYSTERY_BOX_TYPE.STARTER]: 0,
      [MYSTERY_BOX_TYPE.POWER]: 0,
      [MYSTERY_BOX_TYPE.MASTER]: 0,
      [MYSTERY_BOX_TYPE.ELITE]: 0,
      [MYSTERY_BOX_TYPE.LEGENDARY]: 0,
    },
    revealedMysteryBoxes: 0,
    collectibles: {
      ownedIds: [],
      tap: {
        currentSkinId: null,
      },
    },
  },
  inventory: {
    [INVENTORY_ID.TGE_ALLOCATION_BOOST]: 0,
    [INVENTORY_ID.GOLD_TGE_BOOST_CARD]: 0,
    [INVENTORY_ID.SILVER_TGE_BOOST_CARD]: 0,
    [INVENTORY_ID.BRONZE_TGE_BOOST_CARD]: 0,
    [INVENTORY_ID.DIAMOND_MULTIPLIER]: 0,
    [INVENTORY_ID.PLATINUM_PASS]: 0,
    [INVENTORY_ID.EMBER_ELEMENT_SHARD]: 0,
    [INVENTORY_ID.FROST_ELEMENT_SHARD]: 0,
    [INVENTORY_ID.VOLT_ELEMENT_SHARD]: 0,
    [INVENTORY_ID.STONE_ELEMENT_SHARD]: 0,
    [INVENTORY_ID.ELEMENT_MULTIPLIER]: 0,
    [INVENTORY_ID.RAINBOW_KEY]: 0,
    [INVENTORY_ID.WINTER_CROWN]: 0,
    [INVENTORY_ID.PRIME_SIGIL]: 0,
  },
  reminders: {},
  settings: {
    notifications: {
      energyRefilled: true,
      poke: true,
      offlineProfit: true,
      referral: true,
    },
  },
  simpleActions: {},
  gifts: {},
  upgradeCount: 0,
  migrationVersion: 0,
  miniGameCompletions: {},
  specialCurrenciesLastDailyTimestamp: {},
} satisfies PlayerStats)

const offlineProfit = useLocalStorage<number>(buildDropeeStorageKey('offlineProfit'), 0)
const offlineProfitHours = ref(0)
const notifications = ref<Notification[]>([])
const notificationReward = ref<{ coins: number, spins: number, tphX2: number, tickets: number }>({
  coins: 0,
  spins: 0,
  tphX2: 0,
  tickets: 0,
})

export const usePlayerProfit = () => {
  const { now } = useNow()

  const extraOfflineProfit = computed(() => player.value.enabledFeatures.offlineProfit?.amount ?? 0)
  const hasExtraOfflineProfit = computed(() => !!extraOfflineProfit.value)
  const formattedExtraOfflineProfit = computed(() => hasExtraOfflineProfit.value ? `+${formatDuration(extraOfflineProfit.value, 'hh:mm:ss')}` : '')

  const doubleTradingPerHoursSeconds = computed(() => !player.value.enabledFeatures.doubleProfit?.until ? 0 : Math.max(0, player.value.enabledFeatures.doubleProfit.until - now.value))
  const formattedDoubleProfitCountdown = (long = false) => doubleTradingPerHoursSeconds.value > 0 ? formatDuration(doubleTradingPerHoursSeconds.value, long ? 'hh:mm:ss' : 'hh:mm') : ''
  const hasDoubleProfit = computed(() => doubleTradingPerHoursSeconds.value > 0)

  const playerProfit = computed(() => (hasDoubleProfit.value ? 2 : 1) * player.value.profit)

  const activeBoostersCount = computed(() => [hasExtraOfflineProfit.value, hasDoubleProfit.value].filter(item => item).length)

  return {
    playerProfit,
    hasExtraOfflineProfit,
    formattedExtraOfflineProfit,
    formattedDoubleProfitCountdown,
    hasDoubleProfit,
    activeBoostersCount,
  }
}

async function sync(isInit: boolean) {
  const utmSource = new URLSearchParams(window.location.search).get('utm_source')
  if (utmSource) {
    notifySpinForUtmSource.value = utmSource
  }

  const syncResponse = await api.sync(isInit, utmSource)
  if (syncResponse.reauth) {
    removeToken()
    throw new CloseApp('Please reopen the app')
  }
  if (syncResponse.wasDormant) {
    wasDormant.value = true
  }
  player.value = syncResponse.playerStats
  offlineProfit.value = syncResponse.offlineProfit
  offlineProfitHours.value = syncResponse.offlineProfitHours
  notifications.value = syncResponse.notifications
  notificationReward.value = syncResponse.rewards
}

export const usePlayerInit = () => {
  const { config } = useConfig()
  const { playerProfit } = usePlayerProfit()

  function getLevel(): { level: number, from: number, to: number } {
    const experience = player.value.experience
    const levelIndex = config.value.game.levels.findIndex(l => experience >= l.from)
    if (levelIndex === -1) {
      console.error('No level found', { experience: experience })
      return {
        level: 1,
        from: 0,
        to: 0,
      }
    }
    const level = config.value.game.levels[levelIndex]
    return {
      ...level,
      to: config.value.game.levels[levelIndex + 1]?.from ?? Infinity,
    }
  }

  const { now } = useNow()
  const lastProfitCalculation = ref(now.value)
  const lastEnergyCalculation = ref(now.value)
  watch(now, (now) => {
    const offlineProfitMaxUntil = Math.max(player.value.enabledFeatures.offlineProfit?.until ?? 0, player.value.lastUpdate + 3 * 3600)
    const offlineProfitUntil = Math.min(offlineProfitMaxUntil, now)
    const energy = player.value.energy
    if (energy.available !== energy.max) {
      player.value.energy.available = Math.min((now - lastEnergyCalculation.value) * config.value.game.energy.refillPerSecond + energy.available, energy.max)
    }
    lastEnergyCalculation.value = offlineProfitUntil

    if (offlineProfitUntil === lastProfitCalculation.value) return
    player.value.coins += ((offlineProfitUntil - lastProfitCalculation.value) * playerProfit.value / 3600)
    lastProfitCalculation.value = offlineProfitUntil
  })

  watch(() => player.value.experience, () => {
    const { level } = getLevel()
    if (level > player.value.level) {
      player.value.level = level
    }
  })
}

const playerStatsAfterDailyCombo = ref<Nullable<PlayerStats>>(null)

export const usePlayer = () => {
  const { triggerEvent } = useAnalytics()
  const { now, nowDateHourTimestamp } = useNow()
  const { config, updateConfig } = useConfig()
  const isUpgrading = ref(false)
  const isDoingTask = ref(false)
  const hasUpgraded = ref(0)
  const newBadges = ref<null | Badge[]>()
  const claimingTaskId = ref<null | string>()
  const { doAnimateCoins } = useAnimateCoins()
  const { startCoinAnimation } = useFlyingCoins()
  const { addHapticFeedback } = useHapticFeedback()
  const { notify } = useToaster()
  const { t } = useTranslate()
  const { onPurchase } = useUpgrade()

  async function doUpgrade(upgradeId: string, price: number) {
    await syncTap()
    doAnimateCoins()
    const { playerStats: newPlayerStats, config: newConfig } = await api.upgrade(upgradeId)
    triggerEvent({ event: 'spend_virtual_currency', value: price })
    triggerEvent({
      event: 'upgrade',
      metadata: {
        upgrade_id: upgradeId,
        level: upgradeId === 'tap' || upgradeId === 'energy' ? newPlayerStats[upgradeId].level : newPlayerStats.upgrades[upgradeId]?.level,
      },
    })

    updateConfig(newConfig)
    const newFoundComboLength = newPlayerStats.challenges.dailyCombo?.foundCombo.length ?? 0
    const previousFoundComboLength = player.value.challenges.dailyCombo?.foundCombo.length ?? 0

    if (newFoundComboLength !== previousFoundComboLength && newFoundComboLength === 3) {
      playerStatsAfterDailyCombo.value = newPlayerStats
      triggerEvent({ event: 'daily_combo_done' })
      return
    }
    updatePlayer(newPlayerStats)
  }

  async function doTask(taskId: string, reward: number) {
    isDoingTask.value = true
    try {
      const { config: newConfig, playerStats: newPlayerStats, error } = await api.doTask(taskId)
      updateConfig(newConfig)
      updatePlayer(newPlayerStats)
      isDoingTask.value = false
      triggerEvent({ event: 'task_done', metadata: { taskId } })
      triggerEvent({ event: 'earn_virtual_currency', value: reward })
      return { success: newPlayerStats.tasks.others[taskId]?.isDone ?? false, error }
    } catch (e) {
      isDoingTask.value = false
      return { success: false, error: undefined }
    }
  }

  async function claimTask(taskId: DAILY_TASK_ID) {
    claimingTaskId.value = taskId
    try {
      const { success, playerStats: newPlayerStats } = await api.claimTask(taskId)
      if (success) {
        updatePlayer(newPlayerStats)
        addHapticFeedback()
        startCoinAnimation()
        notify({ title: t.value('task.completed', 'Success!'), type: 'success' })
        triggerEvent({ event: 'task_done', metadata: { taskId } })
        claimingTaskId.value = null
        return true
      }
      notify({ title: t.value('task.not_completed', `Task isn't completed`), type: 'error' })
      claimingTaskId.value = null
      return false
    } catch (e) {
      claimingTaskId.value = null
      return false
    }
  }

  const canUseRefillBooster = computed(() => {
    if (player.value.refillBooster.firstUseTimestamp > 0 && player.value.refillBooster.firstUseTimestamp + config.value.game.boosters.refill.rechargeCooldown < now.value) {
      player.value.refillBooster.available = player.value.refillBooster.max
      player.value.refillBooster.lastUseTimestamp = 0
      player.value.refillBooster.firstUseTimestamp = 0
    }

    return player.value.refillBooster.available > 0 && nextRefillBoosterAvailableIn.value === 0
  })

  const nextRefillBoosterAvailableIn = computed(() => {
    return Math.max(0, player.value.refillBooster.lastUseTimestamp + config.value.game.boosters.refill.singleUseCooldown - now.value)
  })

  const hasCheckedInToday = computed(() => {
    if (!player.value.tasks.dailyCheckin.lastCheckin) {
      return false
    }
    const today = getNextOccurrence(config.value.game.tasks.dailyCheckin.endHour, nowDateHourTimestamp.value).minus({ second: 1 }).toISODate()
    const lastCheckin = DateTime.fromISO(player.value.tasks.dailyCheckin.lastCheckin, { zone: 'utc' }).toISODate()

    return today === lastCheckin
  })

  function resetPlayerStatsForDailyCombo() {
    if (!playerStatsAfterDailyCombo.value) return
    updatePlayer(playerStatsAfterDailyCombo.value)
    playerStatsAfterDailyCombo.value = null
  }

  function updatePlayer(newPlayerStats: PlayerStats) {
    player.value = newPlayerStats
  }

  return {
    isUpgrading,
    hasUpgraded,
    convertTapToCoin: (tapCount: number): number => {
      const generatedCoins = tapCount * player.value.tap.earnPerTap
      player.value.energy.available -= generatedCoins
      player.value.coins += generatedCoins * player.value.tap.coinPerUnit

      return player.value.energy.available
    },
    coins: computed(() => Math.floor(player.value.coins)),
    experience: computed(() => player.value.experience),
    offlineProfit: computed(() => Math.floor(offlineProfit.value)),
    offlineProfitHours,
    notifications,
    notificationReward,
    checkin: async (purchaseNonce?: string) => {
      const { playerStats } = await api.checkin(purchaseNonce)
      triggerEvent({ event: 'daily_checkin_done' })
      updatePlayer(playerStats)
    },
    goldenCoinValue: computed(() => Math.max(100_000, player.value.profit)),
    upgrade: async (upgradeId: string, price: number, ecommercePrice: EcommerceItemPrice | null = null) => {
      const upgrade = config.value.upgrades.find(u => u.id === upgradeId)
      if (!upgrade) {
        console.error(`Upgrade ${upgradeId} not found`)
        return
      }
      // if (upgrade.price > player.value.coins) return
      isUpgrading.value = true
      try {
        if (ecommercePrice) {
          const upgrade = await onPurchase(upgradeId, ecommercePrice, SPECIAL_ECOMMERCE_ITEM_ID.CARD_UPGRADE)
          if (!upgrade) return
        } else {
          await doUpgrade(upgradeId, price)
        }
        hasUpgraded.value++
      } finally {
        isUpgrading.value = false
      }
    },
    upgradeTap: async (upgradeId: 'tap' | 'energy') => {
      if (config.value.tap[upgradeId].price > player.value.coins) return

      await doUpgrade(upgradeId, config.value.tap[upgradeId].price)
    },
    canUpgradeTap: (upgradeId: 'tap' | 'energy') => {
      return config.value.tap[upgradeId].price <= player.value.coins
    },
    canUseRefillBooster,
    nextRefillBoosterAvailableIn,
    useRefillBooster: async () => {
      if (!canUseRefillBooster.value) return
      const { playerStats: newPlayerStats } = await api.useBooster()
      updatePlayer(newPlayerStats)
    },
    player: player,
    levelProgressPercentage: computed(() => {
      const thisLevel = config.value.game.levels.find(l => l.level === player.value.level)
      const nextLevel = config.value.game.levels.find(l => l.level === player.value.level + 1)
      if (!nextLevel || !thisLevel) return 0
      return Math.floor((player.value.experience - thisLevel.from) * 100 / (nextLevel.from - thisLevel.from))
    }),
    chosenToken: computed(() => (player.value.token ?? defaultToken) as Token),
    doTask,
    isDoingTask,
    hasCheckedInToday: hasCheckedInToday,
    playerStatsAfterDailyCombo,
    resetPlayerStatsForDailyCombo,
    updatePlayer,
    newBadges,
    claimingTaskId,
    claimTask: claimTask,
    totalPurchase: computed(() => (player.value.ecommerce.totalSpentCents / 100) * 120 / 100),
    totalTransactions: computed(() => player.value.ecommerce.tonTransactionsCount),
    totalPathways: computed(() => player.value.pathwayEvents.completionsCount),
    feathers: computed(() => player.value.specialCurrencies[SPECIAL_CURRENCY_ID.GOLD_FEATHER] ?? 0),
    goldenEggs: computed(() => player.value.specialCurrencies[SPECIAL_CURRENCY_ID.GOLDEN_EGG] ?? 0),
  }
}

export async function syncPlayer(isInit?: boolean) {
  await sync(isInit ?? false)
}
